import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form, Dropdown, Modal } from 'react-bootstrap';
import Pager from './Pager';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import API from '../services/api';
import { canEditInvoice } from '../utils/invoiceEdit';
import ApprovalChip from './ApprovalChip';
import { useAuth } from '../context/AuthContext';
import CreditNoteModal from './CreditNoteModal';
import DebitNoteModal from './DebitNoteModal';
import { bareDate, formatDay, todayDR } from '../utils/dates';
import PagedRows from './PagedRows';
import { nextBillingDate, billingNote } from '../utils/rentalBilling';
import Loading from './Loading';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);
// Rental dates are bare dates (midnight UTC) — see utils/dates.js.
const rentalDate = bareDate;
// The "Facturar" cell: the date the next factura is due and "hoy" / "en N días" / "atrasado N días" (utils/rentalBilling.js),
// as on the Alquileres page.
const BillingDate = ({ rental }) => {
  const date = nextBillingDate(rental);
  const note = billingNote(date);
  return (
    <>
      {date ? rentalDate(date) : '—'}
      {note && <div className="small" style={{ color: note.color }}>{note.text}</div>}
    </>
  );
};
const RENTAL_ROWS = 5; // rows per page in each rental worklist, so the section doesn't push the facturas far down
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const STATUS_VARIANT = {
  draft: 'secondary',
  ready: 'info',
  pending_dgii: 'info',
  issued: 'primary',
  rejected: 'danger',
  paid: 'success',
  partially_paid: 'warning',
  void: 'danger',
};

const STATUS_LABEL = {
  draft: 'Borrador',
  ready: 'Lista',
  pending_dgii: 'Verificando DGII...',
  issued: 'Emitida',
  rejected: 'Rechazada DGII',
  paid: 'Pagada',
  partially_paid: 'Pago Parcial',
  void: 'Anulada',
};

// DGII accepted it with an observation ("Aceptado Condicional", e.g. a buyer RNC/cédula that isn't a real taxpayer): it is a
// valid e-CF — only worth fixing for next time (backend/services/ecf/dgiiOutcome.js).
const conditionallyAccepted = (inv) => inv.ecf?.status === 'accepted'
  && /^aceptado\s*condicional/i.test(String(inv.ecf?.dgiiStatus || '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

const STATUS_COLOR = {
  draft: '#9DB3C2',
  ready: '#5CC8F5',
  pending_dgii: '#5CC8F5',
  issued: '#60A5FA',
  rejected: '#F87171',
  paid: '#4ADE80',
  partially_paid: '#F5B700',
  void: '#F87171',
};

// An admin refusing a request to send an invoice to DGII, optionally saying why; the sales user sees the reason on the invoice.
const DenySendModal = ({ invoice, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setBusy(true); setError('');
    try { const { data } = await API.post(`/invoices/${invoice._id}/deny-send`, { reason }); onDone(data.message); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo denegar la solicitud.'); setBusy(false); }
  };
  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton><Modal.Title>Denegar envío a DGII</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="mb-2">Factura <strong>{invoice.number}</strong> ({invoice.client?.name || 'sin cliente'}, {money(invoice.total)}), pedida por {invoice.sendApproval?.requestedBy}.</p>
        <p className="small text-muted">No se envía a DGII y queda como "Lista". Quien la pidió verá tu motivo y podrá corregirla y volver a pedirla.</p>
        <Form.Label className="small text-muted mb-1">Motivo <span className="text-muted">(opcional)</span></Form.Label>
        <Form.Control as="textarea" rows={2} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej.: el RNC del cliente está mal; corrígelo primero." />
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button variant="danger" disabled={busy} onClick={submit}>{busy ? 'Denegando...' : 'Denegar'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false); // the first load of the list has come back
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sendingId, setSendingId] = useState(null);
  const [sendError, setSendError] = useState('');
  const [pendingInvoiceRentals, setPendingInvoiceRentals] = useState([]);
  const [dueForRechargeRentals, setDueForRechargeRentals] = useState([]);
  const [endingSoonRentals, setEndingSoonRentals] = useState([]);
  const [rentalError, setRentalError] = useState('');
  const [rentalSearch, setRentalSearch] = useState('');
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [denying, setDenying] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [debitFor, setDebitFor] = useState(null);
  const [ranges, setRanges] = useState([]);
  // Protocolo 514 (Administración → Rangos e-NCF): a consumidor final factura issued without going to DGII (no e-NCF).
  // Its marks and actions exist ONLY while the protocol is on; with it off these facturas look like any other.
  const [protocolOn, setProtocolOn] = useState(false);
  useEffect(() => { API.get('/invoices/internal-e32').then(res => setProtocolOn(!!res.data.enabled)).catch(() => {}); }, []);
  const isInternal = (inv) => protocolOn && !!inv.internal && ['issued', 'paid', 'partially_paid'].includes(inv.status);
  // An internal factura is sent to DGII by admins only (sales don't get the button; the server refuses them too).
  const sendable = (inv) => inv.status === 'ready' || (isAdmin && isInternal(inv) && inv.ecf?.status !== 'pending');
  // Waiting for an admin to approve (and thereby send) a sales user's request; or denied, with a reason.
  const waitingApproval = (inv) => sendable(inv) && inv.sendApproval?.status === 'requested';
  const deniedApproval = (inv) => sendable(inv) && inv.sendApproval?.status === 'denied';
  const pageSize = 10;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get('client');
  const clientFilterName = searchParams.get('clientName');

  const loadRentalWorklists = () => {
    API.get('/rentals', { params: { worklist: 'pending-invoice' } }).then(res => setPendingInvoiceRentals(res.data)).catch(err => console.error('Error loading pending-invoice rentals:', err));
    API.get('/rentals', { params: { worklist: 'due-for-recharge' } }).then(res => setDueForRechargeRentals(res.data)).catch(err => console.error('Error loading due-for-recharge rentals:', err));
    API.get('/rentals', { params: { worklist: 'ending-soon' } }).then(res => setEndingSoonRentals(res.data)).catch(err => console.error('Error loading ending-soon rentals:', err));
  };

  // silent: the 10-second refresh below, which shouldn't flash the activity bar.
  const reloadInvoices = (quiet) => { const silent = quiet === true; return Promise.all([
    API.get('/invoices', { silent }).then(res => setInvoices(res.data)).catch(err => console.error('Error loading invoices:', err)),
    API.get('/invoices/ncf-ranges', { silent }).then(res => setRanges(res.data)).catch(err => console.error('Error loading e-NCF ranges:', err)),
  ]).finally(() => setLoaded(true)); };

  useEffect(() => {
    const load = reloadInvoices;
    load(false);
    loadRentalWorklists();
    // DGII's verdict can arrive a few seconds after sending (see
    // backend/jobs/dgiiStatusCheck.js) — poll so "Verificando DGII..." rows
    // resolve to Emitida/Rechazada on their own, no manual refresh needed.
    const interval = setInterval(() => load(true), 10000);
    return () => clearInterval(interval);
  }, []);

  // Opens Nueva factura filled in from the rental (client, comprobante, lines, period); it is created when saved.
  const handleGenerateRentalInvoice = (rentalId) => navigate(`/invoices/new?rental=${rentalId}`);

  // Voiding from this list is for internal comprobantes only (nothing at DGII to modify with a note).
  // Admins: a factura that never reached DGII (no NCF, never sent, no cobros) can be deleted. The server re-checks everything
  // (DELETE /invoices/:id) and gives a rental its period back.
  const deletable = (inv) => isAdmin && !inv.legacy?.source && !inv.ncf && (!inv.ecf?.status || inv.ecf.status === 'not_sent')
    && ['draft', 'ready', 'void'].includes(inv.status) && !(inv.amountPaid > 0.005) && !waitingApproval(inv);
  const handleDelete = async (inv) => {
    if (!window.confirm(`¿Eliminar la factura ${inv.number} de ${inv.client?.name || 'sin cliente'} (${money(inv.total)})? Nunca se envió a DGII. No se puede deshacer.`)) return;
    setSendError(''); setNotice('');
    try {
      const { data } = await API.delete(`/invoices/${inv._id}`);
      setNotice(data.message);
      await reloadInvoices();
      loadRentalWorklists();
    } catch (err) {
      setSendError(err.response?.data?.message || 'No se pudo eliminar la factura.');
    }
  };

  const handleVoidInternal = async (inv) => {
    if (!window.confirm(`¿Anular la factura interna ${inv.number} de ${inv.client?.name || 'sin cliente'}? No se puede deshacer.`)) return;
    setSendError(''); setNotice('');
    try {
      const res = await API.post(`/invoices/${inv._id}/void`);
      if (res.status !== 202) setNotice(`Factura ${inv.number} anulada.`); // 202 = a sales user asked for approval (the toast says it)
      await reloadInvoices();
    } catch (err) {
      setSendError(err.response?.data?.message || 'No se pudo anular la factura.');
    }
  };

  // The list carries no lines (see GET /invoices): a note's modal gets the whole factura.
  const openNote = async (inv, open) => {
    setSendError('');
    try {
      const { data } = await API.get(`/invoices/${inv._id}`);
      open(data);
    } catch (err) {
      setSendError(err.response?.data?.message || 'No se pudo abrir la factura.');
    }
  };

  const handleSendEcf = async (id) => {
    const target = invoices.find(inv => inv._id === id);
    if (target && isInternal(target) && !window.confirm(`La factura ${target.number} es un comprobante interno. Si la envías a DGII se emite con la fecha de hoy, usa un e-NCF de Ventas y queda reportada desde aquí: no la reportes también en el sistema anterior. ¿Continuar?`)) return;
    setSendError('');
    setNotice('');
    setSendingId(id);
    try {
      const { data } = await API.post(`/invoices/${id}/send-ecf`);
      if (data.approvalRequested) {
        // A sales user's request: their part is done. The invoice now shows "Esperando aprobación" until an admin decides.
        setNotice(data.alreadyRequested
          ? data.message
          : `Pediste aprobación para enviar la factura ${data.invoice.number} a DGII. Un administrador la revisará y la enviará: no tienes que esperar. Mientras tanto aparece como "Esperando aprobación".${data.emailed > 0 ? '' : ` ${data.emailNote || ''} Los administradores la verán en esta lista.`}`);
        await reloadInvoices();
      } else {
        setInvoices(prev => prev.map(inv => inv._id === id ? { ...inv, ...data.invoice } : inv));
      }
    } catch (err) {
      setSendError(err.response?.data?.message || 'Error al enviar la factura a DGII.');
    } finally {
      setSendingId(null);
    }
  };

  // The rental worklists' search: number, client (name / RNC / cédula) or equipment, without accents or case.
  const rentalMatch = useMemo(() => {
    const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const term = norm(rentalSearch.trim());
    return (r) => !term || [r.number, r.client?.name, r.client?.rnc, r.client?.cedula, ...(r.items || []).map(i => i.description)].some(v => norm(v).includes(term));
  }, [rentalSearch]);
  const shownPending = pendingInvoiceRentals.filter(rentalMatch);
  // What's due today first ("toca hoy"), then the overdue ones, oldest first.
  const shownDue = dueForRechargeRentals.filter(rentalMatch).sort((a, b) => {
    const today = todayDR();
    const da = String(a.nextRechargeDate || '').slice(0, 10);
    const db = String(b.nextRechargeDate || '').slice(0, 10);
    return ((da >= today) === (db >= today) ? 0 : da >= today ? -1 : 1) || da.localeCompare(db);
  });
  const shownEnding = endingSoonRentals.filter(rentalMatch);

  // The summary at the top: the company's for admins; a sales user's OWN facturas (they can still open every factura below).
  const stats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const counted = isAdmin ? invoices : invoices.filter(inv => inv.createdBy === user?.username);
    const active = counted.filter(inv => inv.status !== 'void' && inv.status !== 'rejected');

    const facturadoHoy = active
      .filter(inv => new Date(inv.createdAt) >= todayStart)
      .reduce((sum, inv) => sum + inv.total, 0);

    const monthly = {};
    active.forEach(inv => {
      const d = new Date(inv.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthly[key] = (monthly[key] || 0) + inv.total;
    });
    const monthlyLabels = [];
    const monthlyValues = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthlyLabels.push(`${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`);
      monthlyValues.push(monthly[key] || 0);
    }

    const byStatus = { draft: 0, ready: 0, pending_dgii: 0, issued: 0, rejected: 0, paid: 0, partially_paid: 0, void: 0 };
    counted.forEach(inv => { byStatus[inv.status] = (byStatus[inv.status] || 0) + 1; });

    return {
      facturadoHoy,
      count: active.filter(inv => !['E33', 'E34'].includes(inv.tipoEcf)).length,
      monthlyLabels,
      monthlyValues,
      byStatus,
    };
  }, [invoices, isAdmin, user?.username]);

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (clientFilter) list = list.filter(inv => inv.client?._id === clientFilter);
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(inv =>
      inv.number?.toLowerCase().includes(term) ||
      inv.ncf?.toLowerCase().includes(term) ||
      inv.client?.name?.toLowerCase().includes(term)
    );
  }, [invoices, search, clientFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const pagedInvoices = filteredInvoices.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const monthlyChart = {
    labels: stats.monthlyLabels,
    datasets: [{ label: 'Facturado', data: stats.monthlyValues, backgroundColor: 'rgba(92,200,245,0.65)', borderRadius: 4 }],
  };

  const statusKeys = Object.keys(STATUS_LABEL);
  const statusChart = {
    labels: statusKeys.map(k => STATUS_LABEL[k]),
    datasets: [{
      data: statusKeys.map(k => stats.byStatus[k] || 0),
      backgroundColor: statusKeys.map(k => STATUS_COLOR[k]),
      hoverOffset: 4,
    }],
  };

  // Like Manifold Manager: nothing but the loading indicator until the page's data has come back.
  if (!loaded) return <Loading page label="Cargando facturas..." />;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="app-title" style={{ textAlign: 'left', margin: 0 }}>Facturas</h2>
        <Button onClick={() => navigate('/invoices/new')}>+ Nueva Factura</Button>
      </div>

      <Row className="mb-3">
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">{isAdmin ? 'Facturado Hoy' : 'Facturado por ti hoy'}</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(stats.facturadoHoy)}</h2>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">{isAdmin ? 'Facturas Emitidas' : 'Tus facturas emitidas'}</div>
            <h2 className="mt-2 mb-0">{stats.count}</h2>
          </Card>
        </Col>
      </Row>

      {ranges.map(r => {
        const low = r.remaining <= Math.max(10, Math.ceil(r.total * 0.1));
        const expiring = r.expiresInDays !== null && r.expiresInDays <= 60;
        return (
          <React.Fragment key={r.tipoEcf}>
            {low && (
              <div className={`alert ${r.remaining === 0 ? 'alert-danger' : 'alert-warning'}`}>
                {r.remaining === 0
                  ? `Se agotó el rango de e-NCF ${r.tipoEcf}: no se pueden emitir más comprobantes ${r.tipoEcf} hasta que DGII autorice un nuevo rango.`
                  : `Quedan ${r.remaining} e-NCF ${r.tipoEcf} de ${r.total} autorizados. Solicita un nuevo rango en la Oficina Virtual de DGII antes de que se agote.`}
              </div>
            )}
            {expiring && (
              <div className={`alert ${r.expiresInDays < 0 ? 'alert-danger' : 'alert-warning'}`}>
                {r.expiresInDays < 0
                  ? `El rango de e-NCF ${r.tipoEcf} venció el ${r.expiration}.`
                  : `El rango de e-NCF ${r.tipoEcf} vence el ${r.expiration} (en ${r.expiresInDays} días).`}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {isAdmin && invoices.some(waitingApproval) && (
        <div className="alert alert-warning d-flex align-items-center gap-2">
          <i className="bi bi-hourglass-split" />
          <span><strong>{invoices.filter(waitingApproval).length}</strong> {invoices.filter(waitingApproval).length === 1 ? 'factura espera' : 'facturas esperan'} tu aprobación para enviarse a DGII: usa "Aprobar y enviar" o "Denegar" en su fila.</span>
        </div>
      )}

      {(pendingInvoiceRentals.length > 0 || dueForRechargeRentals.length > 0 || endingSoonRentals.length > 0) && (
        <Card className="shadow-sm p-3 mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <Card.Title className="mb-0">Alquileres</Card.Title>
            <Form.Control size="sm" style={{ maxWidth: 320 }} placeholder="Buscar alquiler, cliente o equipo..." value={rentalSearch} onChange={e => setRentalSearch(e.target.value)} />
          </div>
          {rentalError && <div className="alert alert-danger">{rentalError}</div>}
          {rentalSearch.trim() && !shownPending.length && !shownDue.length && !shownEnding.length && (
            <div className="text-muted small mb-2">Ningún alquiler coincide con "{rentalSearch.trim()}".</div>
          )}

          {shownPending.length > 0 && (
            <>
              <div className="text-muted small mb-1">Listos para facturar (primera factura)</div>
              <PagedRows rows={shownPending} size={RENTAL_ROWS}>{(rows) => (
              <Table hover responsive size="sm" className="mb-0">
                <thead><tr><th>No.</th><th>Cliente</th><th>Equipo</th><th title="Cuándo toca hacer la próxima factura del alquiler">Facturar</th><th className="text-end">Monto/Ciclo</th><th></th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r._id}>
                      <td>{r.number}</td>
                      <td>{r.client?.name || '—'}</td>
                      <td>{(r.items || []).map(i => i.description).join(', ') || '—'}</td>
                      <td className="text-nowrap"><BillingDate rental={r} /></td>
                      <td className="text-end">{money(r.total)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleGenerateRentalInvoice(r._id)}
                        >
                          Generar Factura
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              )}</PagedRows>
              <div className="mb-3" />
            </>
          )}

          {shownDue.length > 0 && (
            <>
              <div className="text-muted small mb-1">Alquileres por renovar</div>
              <PagedRows rows={shownDue} size={RENTAL_ROWS}>{(rows) => (
              <Table hover responsive size="sm" className="mb-0">
                <thead><tr><th>No.</th><th>Cliente</th><th>Equipo</th><th title="Cuándo toca hacer la próxima factura del alquiler">Facturar</th><th className="text-end">Monto/Ciclo</th><th></th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r._id}>
                      <td>{r.number}</td>
                      <td>{r.client?.name || '—'}</td>
                      <td>{(r.items || []).map(i => i.description).join(', ') || '—'}</td>
                      <td className="text-nowrap"><BillingDate rental={r} /></td>
                      <td className="text-end">{money(r.total)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleGenerateRentalInvoice(r._id)}
                        >
                          Renovar alquiler
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              )}</PagedRows>
              <div className="mb-3" />
            </>
          )}

          {shownEnding.length > 0 && (
            <>
              <div className="text-muted small mb-1">Fin de alquiler próximo / vencido (coordinar devolución)</div>
              <PagedRows rows={shownEnding} size={RENTAL_ROWS}>{(rows) => (
              <Table hover responsive size="sm" className="mb-0">
                <thead><tr><th>No.</th><th>Cliente</th><th>Equipo</th><th>Fecha Fin</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r._id}>
                      <td>{r.number}</td>
                      <td>{r.client?.name || '—'}</td>
                      <td>{(r.items || []).map(i => i.description).join(', ') || '—'}</td>
                      <td>{rentalDate(r.endDate)}</td>
                      <td style={{ color: r.daysOverdue > 0 ? 'var(--mm-red)' : (r.daysUntilEnd > 0 && r.daysUntilEnd <= 7 ? 'var(--mm-yellow)' : 'var(--mm-muted)') }}>
                        {r.daysOverdue > 0 ? `Vencido hace ${r.daysOverdue} día(s)` : r.daysUntilEnd > 0 ? `En ${r.daysUntilEnd} día(s)` : '—'}
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/rentals/${r._id}/edit`)}>
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              )}</PagedRows>
            </>
          )}
        </Card>
      )}

      <Card className="shadow-sm p-3">
        {notice && (
          <div className="alert alert-success d-flex justify-content-between align-items-start">
            <span>{notice}</span>
            <button type="button" className="close" aria-label="Cerrar" onClick={() => setNotice('')}><span aria-hidden="true">&times;</span></button>
          </div>
        )}
        {sendError && <div className="alert alert-danger">{sendError}</div>}
        {clientFilter && (
          <div className="d-flex align-items-center gap-2 mb-3">
            <Badge bg="info">Cliente: {clientFilterName || '...'}</Badge>
            <Button size="sm" variant="link" className="p-0" onClick={() => setSearchParams({})}>Quitar filtro</Button>
          </div>
        )}
        <Form.Control
          className="mb-3"
          style={{ maxWidth: 320 }}
          placeholder="Buscar por número, NCF o cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th>No.</th><th>NCF</th><th>Cliente</th><th>Fecha</th><th>Vencimiento</th>
              <th className="text-end">Total</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pagedInvoices.map(inv => (
              <tr key={inv._id}>
                <td>
                  {inv.number}
                  {['E33', 'E34'].includes(inv.tipoEcf) && <div className="text-muted small">Modifica {inv.modifiedNcf}</div>}
                  {isInternal(inv) && <div className="text-muted small" title="Protocolo 514: no se envió a DGII y no tiene e-NCF">Interna{inv.ecf?.status === 'pending' ? ' · verificando DGII...' : ''}</div>}
                  {inv.legacy?.source && <div className="text-muted small" title="Factura del sistema anterior (SGC2000): NCF en papel, no se envía a DGII">Sistema anterior</div>}
                  <div><ApprovalChip entityId={inv._id} /></div>
                </td>
                <td>{inv.ncf || (isInternal(inv) ? <span className="text-muted">Interna</span> : '—')}</td>
                <td>{inv.client?.name || '—'}</td>
                <td>{dateStr(inv.createdAt)}</td>
                <td>{dateStr(inv.dueDate)}</td>
                <td className="text-end">{money(inv.total)}</td>
                <td>
                  {inv.creditedAmount > 0 && inv.creditedAmount >= inv.total - 0.005 ? (
                    <Badge bg="secondary">Anulada (NC)</Badge>
                  ) : waitingApproval(inv) ? (
                    <Badge bg="warning" title={`Pedida por ${inv.sendApproval.requestedBy}: un administrador la aprobará y la enviará a DGII`}>Esperando aprobación</Badge>
                  ) : (
                    <Badge bg={STATUS_VARIANT[inv.status]} title={inv.status === 'rejected' ? inv.ecf?.dgiiMessage : undefined}>
                      {STATUS_LABEL[inv.status] || inv.status}
                    </Badge>
                  )}
                  {waitingApproval(inv) && <div className="text-muted small">pedida por {inv.sendApproval.requestedBy}</div>}
                  {deniedApproval(inv) && (
                    <div className="small mt-1" style={{ color: 'var(--mm-red)' }} title={inv.sendApproval.decisionReason || undefined}>
                      Envío denegado por {inv.sendApproval.decidedBy}{inv.sendApproval.decisionReason ? `: “${inv.sendApproval.decisionReason}”` : ''}
                    </div>
                  )}
                  {conditionallyAccepted(inv) && (
                    <div className="small mt-1" style={{ color: 'var(--mm-yellow)' }} title={inv.ecf.dgiiMessage || undefined}>
                      <i className="bi bi-exclamation-circle me-1" />Aceptada con observaciones{inv.ecf.dgiiMessage ? `: ${inv.ecf.dgiiMessage}` : ''}
                    </div>
                  )}
                  {isInternal(inv) && inv.ecf?.status === 'rejected' && inv.ecf?.dgiiMessage && (
                    <div className="small mt-1" style={{ color: 'var(--mm-red)' }}>{inv.ecf.dgiiMessage}</div>
                  )}
                  {inv.creditedAmount > 0 && <div className="text-muted small">Acreditada {money(inv.creditedAmount)}</div>}
                </td>
                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    {canEditInvoice(inv, { isAdmin, protocolOn }) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => navigate(`/invoices/${inv._id}/edit`)}
                      >
                        <i className="bi bi-pencil" /> Editar
                      </Button>
                    )}
                    {waitingApproval(inv) && !isAdmin && (
                      <Button size="sm" variant="outline-secondary" disabled title="Un administrador la aprobará y la enviará a DGII">
                        <i className="bi bi-hourglass-split" /> Esperando aprobación
                      </Button>
                    )}
                    {waitingApproval(inv) && isAdmin && (
                      <>
                        <Button size="sm" variant="success" disabled={sendingId === inv._id} onClick={() => handleSendEcf(inv._id)}>
                          {sendingId === inv._id ? 'Enviando...' : 'Aprobar y enviar'}
                        </Button>
                        <Button size="sm" variant="outline-danger" disabled={sendingId === inv._id} onClick={() => { setNotice(''); setDenying(inv); }}>Denegar</Button>
                      </>
                    )}
                    {sendable(inv) && !waitingApproval(inv) && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={sendingId === inv._id}
                        onClick={() => handleSendEcf(inv._id)}
                        title={isAdmin || inv.modifiesInvoice ? undefined : 'Un administrador la aprobará y la enviará a DGII'}
                      >
                        {sendingId === inv._id ? (isAdmin || inv.modifiesInvoice ? 'Enviando...' : 'Pidiendo...')
                          : (isAdmin || inv.modifiesInvoice ? 'Enviar a DGII' : deniedApproval(inv) ? 'Volver a pedir envío' : 'Pedir envío a DGII')}
                      </Button>
                    )}
                    {['E31', 'E32'].includes(inv.tipoEcf) && ['issued', 'paid', 'partially_paid'].includes(inv.status)
                      && !isInternal(inv) /* same for an internal comprobante: it is voided instead */
                      && !inv.legacy?.source /* history from the old system has no e-CF, so DGII has nothing to modify */
                      && inv.total - (inv.creditedAmount || 0) > 0.005 && (
                      <Dropdown align="end">
                        <Dropdown.Toggle size="sm" variant="outline-warning">Nota</Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                          <Dropdown.Item onClick={() => openNote(inv, setCreditFor)}>Nota de crédito</Dropdown.Item>
                          <Dropdown.Item onClick={() => openNote(inv, setDebitFor)}>Nota de débito</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    {isInternal(inv) && inv.ecf?.status !== 'pending' && !(inv.amountPaid > 0.005) && (
                      <Button size="sm" variant="outline-danger" onClick={() => handleVoidInternal(inv)}>Anular</Button>
                    )}
                    {deletable(inv) && (
                      <Button size="sm" variant="outline-danger" title="Nunca se envió a DGII" onClick={() => handleDelete(inv)}>
                        <i className="bi bi-trash" /> Eliminar
                      </Button>
                    )}
                    {inv.status !== 'draft' && inv.status !== 'ready' ? (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => window.open(`/invoices/${inv._id}/print`, '_blank')}
                      >
                        <i className="bi bi-printer" /> Ver / Imprimir
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline-secondary" title="Cómo quedará la factura (no se imprime hasta que DGII la acepte)"
                        onClick={() => window.open(`/invoices/${inv._id}/preview`, '_blank')}>
                        <i className="bi bi-eye" /> Vista previa
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredInvoices.length === 0 && (
              <tr><td colSpan={8} className="text-muted text-center">
                {!loaded ? <Loading label="Cargando facturas..." /> : search ? `Ninguna factura coincide con "${search}".` : 'Sin facturas todavía.'}
              </td></tr>
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Página {currentPage} de {totalPages} — {filteredInvoices.length} factura{filteredInvoices.length === 1 ? '' : 's'}
            </span>
            <Pager page={currentPage} pages={totalPages} onChange={setCurrentPage} />
          </div>
        )}
      </Card>

      {denying && <DenySendModal invoice={denying} onClose={() => setDenying(null)} onDone={(message) => { setDenying(null); setNotice(message); reloadInvoices(); }} />}
      {creditFor && (
        <CreditNoteModal invoice={creditFor} onClose={() => setCreditFor(null)} onChanged={reloadInvoices} />
      )}
      {debitFor && (
        <DebitNoteModal invoice={debitFor} onClose={() => setDebitFor(null)} onChanged={reloadInvoices} />
      )}

      <Row className="mt-3">
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>{isAdmin ? 'Facturación por Mes' : 'Tu facturación por mes'}</Card.Title>
            <div style={{ height: 280 }}>
              <Bar
                data={monthlyChart}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </div>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>{isAdmin ? 'Facturas por Estado' : 'Tus facturas por estado'}</Card.Title>
            <div style={{ height: 280 }}>
              <Doughnut
                data={statusChart}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InvoicesPage;
